import { Injectable } from '@angular/core';
import { DEFAULT_BANK, Question, QuizBank, QuizMode, bankKey } from '../models/quiz';

export interface QuizResult {
  quizId: number;
  /** Missing in results saved before the 2026 edition was added (the removed 2022 tests). */
  bank?: QuizBank;
  language: string;
  mode: QuizMode;
  questions: Question[];
  /** 0-based option index chosen for each question, null when skipped. */
  answers: (number | null)[];
  score: number;
  totalQuestions: number;
  minPassingScore: number;
  passed: boolean;
  timeUp: boolean;
  durationSeconds: number;
  finishedAt: number;
}

export interface QuizHistoryEntry {
  score: number;
  totalQuestions: number;
  passed: boolean;
  finishedAt: number;
}

/** An unfinished attempt, kept so a page refresh does not lose answers. */
export interface QuizProgress {
  /** Selected 0-based option index keyed by question id. */
  answers: Record<string, number>;
  currentIndex: number;
  /** Epoch ms when the exam time runs out; null in practice mode. */
  deadline: number | null;
  startedAt: number;
}

const RESULT_KEY = 'drivon.quiz.lastResult';
const HISTORY_KEY = 'drivon.quiz.history';
const PROGRESS_KEY = 'drivon.quiz.progress';

@Injectable({
  providedIn: 'root',
})
export class QuizResultsService {
  private result: QuizResult | null = null;

  setResult(result: QuizResult) {
    this.result = result;
    write(sessionStorageSafe(), RESULT_KEY, result);

    if (!result.bank) return;
    const bank = result.bank;
    const history = this.getHistory(bank);
    history[result.quizId] = {
      score: result.score,
      totalQuestions: result.totalQuestions,
      passed: result.passed,
      finishedAt: result.finishedAt,
    };
    write(localStorageSafe(), historyKey(bank), history);
  }

  getResult(): QuizResult | null {
    if (!this.result) {
      this.result = read<QuizResult>(sessionStorageSafe(), RESULT_KEY);
    }
    return this.result;
  }

  /** Last result per quiz id of a bank, used to show progress on the quiz list. */
  getHistory(bank: QuizBank = DEFAULT_BANK): Record<number, QuizHistoryEntry> {
    return read<Record<number, QuizHistoryEntry>>(localStorageSafe(), historyKey(bank)) ?? {};
  }

  /** `language` is the language of the questions, so answers never land on a different question set. */
  getProgress(bank: QuizBank, language: string, quizId: number, mode: QuizMode): QuizProgress | null {
    return read<QuizProgress>(sessionStorageSafe(), progressKey(bank, language, quizId, mode));
  }

  saveProgress(bank: QuizBank, language: string, quizId: number, mode: QuizMode, progress: QuizProgress) {
    write(sessionStorageSafe(), progressKey(bank, language, quizId, mode), progress);
  }

  clearProgress(bank: QuizBank, language: string, quizId: number, mode: QuizMode) {
    try {
      sessionStorageSafe()?.removeItem(progressKey(bank, language, quizId, mode));
    } catch {
      // Storage can be unavailable (private mode, blocked cookies) - nothing to clear then.
    }
  }
}

function historyKey(bank: QuizBank) {
  return `${HISTORY_KEY}.${bankKey(bank)}`;
}

function progressKey(bank: QuizBank, language: string, quizId: number, mode: QuizMode) {
  return `${PROGRESS_KEY}.${bankKey(bank)}.${language}.${mode}.${quizId}`;
}

function sessionStorageSafe(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function localStorageSafe(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function read<T>(storage: Storage | null, key: string): T | null {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(storage: Storage | null, key: string, value: unknown) {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded or storage blocked - the in-memory copy still works for this visit.
  }
}
